/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {vec3} from "gl-matrix";
import TreeNode from "App/utils/globe/chunk_lod/tree/node";
import IAddress from "App/utils/globe/chunk_lod/adress";

export default
class PatchAddress implements IAddress {

    public static startingOffset: { x: number, y: number } = {
        x: 0,//611000, // this is E5
        y: 0//267000
    };

    public base: string;
    public origin: string;
    public patchInfo: any;
    public translationOffset: number[];
    public level: number;
    public layer: number;
    public row: number;
    public col: number;
    public flattenedTreeIndex: number;

    constructor(aValues: any) {
        this.base = null;
        this.origin = null;
        this.patchInfo = null;
        this.translationOffset = [0, 0];
        this.level = null;
        this.layer = null;
        this.row = null;
        this.col = null;

        this.set(aValues);

        this.flattenedTreeIndex = TreeNode.getIndexToFlattenedTree(this);
    }

    public create(aValues: any): PatchAddress {
        return new PatchAddress(aValues);
    }

    public toLevel(aLevel: number): PatchAddress {
        if (this.level >= aLevel) {
            return new PatchAddress(
                {
                    layer: Math.floor(this.layer / Math.pow(2, this.level - aLevel)),
                    row: Math.floor(this.row / Math.pow(2, this.level - aLevel)),
                    col: Math.floor(this.col / Math.pow(2, this.level - aLevel)),
                    level: aLevel
                }
            );
        } else {
            return new PatchAddress(
                {
                    layer: this.layer * Math.pow(2, aLevel - this.level),
                    row: this.row * Math.pow(2, aLevel - this.level),
                    col: this.col * Math.pow(2, aLevel - this.level),
                    level: aLevel
                }
            );
        }
    }

    public getChildIndexOf(aParent: PatchAddress): number {
        return (this.layer - (aParent.layer * 2)) * 2 * 2 + (this.row - (aParent.row * 2)) * 2 + (this.col - (aParent.col * 2));
    }

    public setTranslationOffset(aArray: number[]) {
        this.translationOffset = aArray.slice();
    }

    public getTranslationOffset(): number[] {
        return [this.translationOffset[0] - PatchAddress.startingOffset.x, PatchAddress.startingOffset.y - this.translationOffset[1]];
    }

    public set(aValues: any) {
        for (let key in aValues) {
            if (this.hasOwnProperty(key)) {
                this[key] = aValues[key];
            } else {
                throw new Error("ERROR: Class Address has no member called '" + key + "'");
            }
        }

        // test all member of this class are set
        for (let key in this) {
            if (this[key] === null) {
                throw new Error("ERROR: Class Address has unset member called '" + key + "'");
            }
        }
    }

    public getSize(): number {
        return Math.pow(2.0, this.patchInfo.levels - 1 - this.level) * this.patchInfo.size;
    }

    /**
     * compute the center based on the range item name (from the [a-z][0-
     * @param aParentAddress
     */
    public getCenter(): vec3 {
        let lowLeftCorner = vec3.create();
        let center = vec3.create();

        // low left corner
        lowLeftCorner[0] = this.translationOffset[0] - PatchAddress.startingOffset.x;
        lowLeftCorner[2] = PatchAddress.startingOffset.y - this.translationOffset[1];

        // no we go to local left corner
        let size = this.getSize();

        // the center computation from row and col, in lexocad, the [0, 0] address is in 0, -1000
        lowLeftCorner[0] += this.col * size;
        center[2] = -(lowLeftCorner[0] + (size / 2));

        lowLeftCorner[2] += this.row * size;
        center[0] = lowLeftCorner[2] + (size / 2);

        return center;
    }

    public getLevel(): number {
        return this.level;
    }

    public generateUID(): string {
        return this.patchInfo.name + "-" + this.level + "-" + this.layer + "-" + this.row + "-" + this.col;
    }

    public getDataUrl(): string {
        return this.getBaseURL() + "/" + this.level + "-" + this.layer + "-" + this.row + "-" + this.col + ".dat";
    }

    public getDataBaseUrl(): string {
        return this.base + this.patchInfo.name + "/";
    }

    public getBaseURL(): string {
        if (this.base.indexOf("://") !== -1) {
            return this.base + this.patchInfo.name;
        } else if (this.base.indexOf("./") === 0) {
            return this.origin + this.base.substring(1) + this.patchInfo.name;
        } else {
            return this.origin + this.base + this.patchInfo.name;
        }
    }
}
